import { computeMpmAvailableMpFromOccupancy } from "@/features/hoverIdle/mpm/mpmCapacity";
import type { MpmState } from "@/features/hoverIdle/mpm/mpmState";
import {
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  type HovAmplifierEntryId,
} from "@/features/hoverIdle/hov/hovAmplifierModel";
import { resolveMpmHovTargetSlotCount } from "@/features/hoverIdle/mpm/mpmHovTargetSlots";
import {
  createHovGainUpgradeMpTargetFromSlotIndex,
  toHovGainUpgradeMpTargetKey,
  type HovGainUpgradeMpTarget,
  type HovGainUpgradeMpTargetKey,
} from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import type {
  ExplorationMpSlotDefinition,
  ExplorationMpSlotId,
} from "@/features/hoverIdle/exploration/explorationTypes";

export interface MpmAssignAllAmplifierEntryInput {
  readonly id: HovAmplifierEntryId;
  readonly unlocked: boolean;
  readonly mpAssignable: boolean;
}

export interface MpmAssignAllEvaluationInput {
  readonly mpCapacity: number;
  readonly mpm: MpmState;
  readonly hovAmplifier: {
    readonly entries: readonly MpmAssignAllAmplifierEntryInput[];
  };
}

interface MpmSectionAssignAllStateCommon {
  readonly displayedSlotCount: number;
  readonly requiredMp: number;
  readonly availableMp: number;
  // True when the displayed slot set is large enough for the section to
  // participate in the shared latch ( >= 2 distinct slots ).
  readonly latchEligibleByDisplay: boolean;
  // True when this evaluation, on its own, would latch the shared flag.
  readonly latchedByThisSection: boolean;
  // True when the section should display the Assign All action at all.
  // Standard MPM sections require the shared latch plus >= 2 displayed slots;
  // Exploration follows its explicit purchased-slot-count rule instead.
  // Independent of MP affordability and the section enabled toggle, which
  // only control the disabled state once the action is shown.
  readonly shouldShow: boolean;
  // True when executing Assign All would fully fill the unassigned slots.
  readonly canExecute: boolean;
}

export interface MpmSectionAssignAllState extends MpmSectionAssignAllStateCommon {
  readonly unassignedEntryIds: readonly HovAmplifierEntryId[];
}

export interface MpmHovTargetsAssignAllState extends MpmSectionAssignAllStateCommon {
  readonly unassignedIndices: readonly number[];
}

export interface MpmHovGainUpgradesAssignAllState extends MpmSectionAssignAllStateCommon {
  readonly unassignedTargets: readonly HovGainUpgradeMpTarget[];
}

export interface MpmExplorationAssignAllState extends MpmSectionAssignAllStateCommon {
  readonly unassignedSlotIds: readonly ExplorationMpSlotId[];
}

export type MpmHovTargetsAssignAllEvaluationInput = Pick<
  MpmAssignAllEvaluationInput,
  "mpCapacity" | "mpm"
>;

export type MpmHovGainUpgradesAssignAllEvaluationInput = Pick<
  MpmAssignAllEvaluationInput,
  "mpCapacity" | "mpm"
>;

export interface MpmExplorationAssignAllEvaluationInput {
  readonly mpCapacity: number;
  readonly mpm: MpmState;
  readonly purchasedMpSlots: readonly ExplorationMpSlotDefinition[];
}

// Resolves Assign All evaluation for the Amp Panel section. The "displayed
// slot set" mirrors `createMpmAmplifierPanelSlots`: an available entry is shown
// when currently unlocked or already assigned. Latch eligibility requires at
// least two such slots, so the section is excluded from latch decisions while
// only one Amp (RA) is visible. An assignment preserved behind a player-facing
// availability gate stays in state but does not become a displayed target.
export function resolveMpmAmplifierPanelAssignAllState(
  input: MpmAssignAllEvaluationInput,
): MpmSectionAssignAllState {
  if (!input.mpm.amplifierPanels.unlocked) {
    return EMPTY_SECTION_STATE;
  }
  const assigned = new Set<HovAmplifierEntryId>(
    input.mpm.amplifierPanels.assignments,
  );
  const unlockedEntryIds = new Set<HovAmplifierEntryId>(
    input.hovAmplifier.entries
      .filter((entry) => entry.unlocked && entry.mpAssignable)
      .map((entry) => entry.id),
  );
  const mpAssignableEntryIds = new Set<HovAmplifierEntryId>(
    input.hovAmplifier.entries
      .filter((entry) => entry.mpAssignable)
      .map((entry) => entry.id),
  );
  const displayed: HovAmplifierEntryId[] = [];
  const unassigned: HovAmplifierEntryId[] = [];
  for (const definition of HOV_AMPLIFIER_ENTRY_DEFINITIONS) {
    const entryId = definition.id;
    const isAssigned = assigned.has(entryId);
    if (
      !unlockedEntryIds.has(entryId) &&
      !(isAssigned && mpAssignableEntryIds.has(entryId))
    ) {
      continue;
    }
    displayed.push(entryId);
    if (!isAssigned) {
      unassigned.push(entryId);
    }
  }
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const requiredMp = unassigned.length;
  const latchEligibleByDisplay = displayed.length >= 2;
  const latchedByThisSection =
    latchEligibleByDisplay && requiredMp > 0 && availableMp >= requiredMp;
  const shouldShow = input.mpm.assignAllUnlocked && latchEligibleByDisplay;
  const canExecute = requiredMp > 0 && availableMp >= requiredMp;
  return {
    displayedSlotCount: displayed.length,
    unassignedEntryIds: unassigned,
    requiredMp,
    availableMp,
    latchEligibleByDisplay,
    latchedByThisSection,
    shouldShow,
    canExecute,
  };
}

// Resolves Assign All evaluation for the Hov Targets section. The "displayed
// slot set" mirrors `createMpmHovTargetSlots`: every H1..H(slotCount) tile is
// shown so long as the section is unlocked, regardless of per-cell unlock
// state. Each Hov Target slot costs 1 MP.
export function resolveMpmHovTargetsAssignAllState(
  input: MpmHovTargetsAssignAllEvaluationInput,
): MpmHovTargetsAssignAllState {
  if (!input.mpm.hovTargets.unlocked) {
    return EMPTY_HOV_TARGETS_STATE;
  }
  const slotCount = resolveMpmHovTargetSlotCount(input.mpm.gridUnlock.hov);
  const assigned = new Set<number>(input.mpm.hovTargets.assignments);
  const unassigned: number[] = [];
  for (let index = 0; index < slotCount; index += 1) {
    if (!assigned.has(index)) unassigned.push(index);
  }
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const requiredMp = unassigned.length;
  const latchEligibleByDisplay = slotCount >= 2;
  const latchedByThisSection =
    latchEligibleByDisplay && requiredMp > 0 && availableMp >= requiredMp;
  const shouldShow = input.mpm.assignAllUnlocked && latchEligibleByDisplay;
  const canExecute = requiredMp > 0 && availableMp >= requiredMp;
  return {
    displayedSlotCount: slotCount,
    unassignedIndices: unassigned,
    requiredMp,
    availableMp,
    latchEligibleByDisplay,
    latchedByThisSection,
    shouldShow,
    canExecute,
  };
}

// Resolves Assign All evaluation for the Hov Gain Upgrades section. The
// "displayed slot set" mirrors `createMpmHovGainUpgradeSlots`: HG plus
// H1G..H(slotCount)G when the section is unlocked. Each slot costs 1 MP.
export function resolveMpmHovGainUpgradesAssignAllState(
  input: MpmHovGainUpgradesAssignAllEvaluationInput,
): MpmHovGainUpgradesAssignAllState {
  if (!input.mpm.hovGainUpgrades.unlocked) {
    return EMPTY_HOV_GAIN_UPGRADES_STATE;
  }
  const hovTargetSlotCount = resolveMpmHovTargetSlotCount(
    input.mpm.gridUnlock.hov,
  );
  const displayedSlotCount = hovTargetSlotCount + 1;
  const assignedTargetKeys = new Set<HovGainUpgradeMpTargetKey>(
    input.mpm.hovGainUpgrades.assignments
      .map((target) => toHovGainUpgradeMpTargetKey(target))
      .filter((key): key is HovGainUpgradeMpTargetKey => key !== null),
  );
  const unassigned: HovGainUpgradeMpTarget[] = [];
  for (let slotIndex = 0; slotIndex < displayedSlotCount; slotIndex += 1) {
    const target = createHovGainUpgradeMpTargetFromSlotIndex(slotIndex);
    if (target === null) continue;
    const key = toHovGainUpgradeMpTargetKey(target);
    if (key === null) continue;
    if (assignedTargetKeys.has(key)) continue;
    unassigned.push(target);
  }
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const requiredMp = unassigned.length;
  const latchEligibleByDisplay = displayedSlotCount >= 2;
  const latchedByThisSection =
    latchEligibleByDisplay && requiredMp > 0 && availableMp >= requiredMp;
  const shouldShow = input.mpm.assignAllUnlocked && latchEligibleByDisplay;
  const canExecute = requiredMp > 0 && availableMp >= requiredMp;
  return {
    displayedSlotCount,
    unassignedTargets: unassigned,
    requiredMp,
    availableMp,
    latchEligibleByDisplay,
    latchedByThisSection,
    shouldShow,
    canExecute,
  };
}

export function resolveMpmExplorationAssignAllState(
  input: MpmExplorationAssignAllEvaluationInput,
): MpmExplorationAssignAllState {
  const assignedSlotIds = new Set(input.mpm.exploration.assignedSlotIds);
  const unassignedSlots = input.purchasedMpSlots.filter(
    (slot) => !assignedSlotIds.has(slot.id),
  );
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity: input.mpCapacity,
    state: input.mpm,
  });
  const requiredMp = unassignedSlots.reduce(
    (total, slot) => total + slot.amount,
    0,
  );
  const latchEligibleByDisplay = input.purchasedMpSlots.length >= 2;
  const canExecute = requiredMp > 0 && availableMp >= requiredMp;
  return {
    displayedSlotCount: input.purchasedMpSlots.length,
    unassignedSlotIds: unassignedSlots.map((slot) => slot.id),
    requiredMp,
    availableMp,
    latchEligibleByDisplay,
    latchedByThisSection: false,
    shouldShow: latchEligibleByDisplay,
    canExecute,
  };
}

// Convenience aggregate: returns true when ANY Assign All-aware section
// would latch on this evaluation. Targets / Upgrades / Amp Panel plug into
// the same OR so the shared MPM latch flips on the first section that
// qualifies.
export function shouldLatchMpmAssignAllUnlocked(
  input: MpmAssignAllEvaluationInput,
): boolean {
  return (
    resolveMpmAmplifierPanelAssignAllState(input).latchedByThisSection ||
    resolveMpmHovTargetsAssignAllState(input).latchedByThisSection ||
    resolveMpmHovGainUpgradesAssignAllState(input).latchedByThisSection
  );
}

const EMPTY_SECTION_STATE: MpmSectionAssignAllState = {
  displayedSlotCount: 0,
  unassignedEntryIds: [],
  requiredMp: 0,
  availableMp: 0,
  latchEligibleByDisplay: false,
  latchedByThisSection: false,
  shouldShow: false,
  canExecute: false,
};

const EMPTY_HOV_TARGETS_STATE: MpmHovTargetsAssignAllState = {
  displayedSlotCount: 0,
  unassignedIndices: [],
  requiredMp: 0,
  availableMp: 0,
  latchEligibleByDisplay: false,
  latchedByThisSection: false,
  shouldShow: false,
  canExecute: false,
};

const EMPTY_HOV_GAIN_UPGRADES_STATE: MpmHovGainUpgradesAssignAllState = {
  displayedSlotCount: 0,
  unassignedTargets: [],
  requiredMp: 0,
  availableMp: 0,
  latchEligibleByDisplay: false,
  latchedByThisSection: false,
  shouldShow: false,
  canExecute: false,
};
